import { ServiceManager } from "../services/ServiceManager";
import { COMMANDE_BADGE_METRIC_OPTIONS, isOrangeMailHost } from "../superAdminRules";
import { Field, setField } from "../SuperAdminCommon";
import {
    btnBlackStyle,
    btnOutlineStyle,
    errorStyle,
    inputStyle,
    insetFormStyle,
    labelStyle,
    orangeNoticeStyle,
    textareaStyle,
} from "../superAdminStyles";

function shortcutLabelStyle(enabled) {
    return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        minHeight: "38px",
        padding: "8px 10px",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        background: enabled ? "#eff6ff" : "white",
        color: "#374151",
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
    };
}

function CommandeFields({ editForm, setEditForm }) {
    return (
        <>
            <Field label="Badge Gestion des commandes">
                <select
                    style={inputStyle}
                    value={editForm.commandeBadgeMetric ?? "NON_FAVORITES"}
                    onChange={setField(setEditForm, "commandeBadgeMetric")}
                >
                    {COMMANDE_BADGE_METRIC_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
            </Field>
            <Field label="Raccourci tournées">
                <label style={shortcutLabelStyle(editForm.commandeTourneeShortcutEnabled)}>
                    <input
                        type="checkbox"
                        checked={!!editForm.commandeTourneeShortcutEnabled}
                        onChange={setField(setEditForm, "commandeTourneeShortcutEnabled", e => e.target.checked)}
                        style={{ width: "15px", height: "15px", accentColor: "#2563eb" }}
                    />
                    Volet tournées dans commandes
                </label>
            </Field>
        </>
    );
}

function PharmacyFields({ editForm, setEditForm }) {
    return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "14px" }}>
            <Field label="Nom *">
                <input style={inputStyle} value={editForm.name} onChange={setField(setEditForm, "name")} required />
            </Field>
            <Field label="Adresse">
                <input style={inputStyle} value={editForm.address} onChange={setField(setEditForm, "address")} />
            </Field>
            <Field label="Téléphone">
                <input style={inputStyle} value={editForm.phone} onChange={setField(setEditForm, "phone")} />
            </Field>
            <Field label="Template" hint="optionnel">
                <textarea
                    style={textareaStyle}
                    placeholder="Template spécifique à cette pharmacie…"
                    value={editForm.template}
                    onChange={setField(setEditForm, "template")}
                />
            </Field>
            <Field label="Email (IMAP)">
                <input style={inputStyle} type="email" value={editForm.email} onChange={setField(setEditForm, "email")} />
            </Field>
            <Field label="Serveur IMAP">
                <input style={inputStyle} list="mail-hosts" value={editForm.mailHost} placeholder="imap.gmail.com" onChange={setField(setEditForm, "mailHost")} />
            </Field>
            <Field label="Nouveau mot de passe d'application" hint="vide = conserver">
                <input style={inputStyle} type="password" value={editForm.mailPassword} onChange={setField(setEditForm, "mailPassword")} autoComplete="new-password" />
            </Field>
            <CommandeFields editForm={editForm} setEditForm={setEditForm} />
        </div>
    );
}

const partenaireChipStyle = { display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 8px", background: "#e0f2fe", color: "#0369a1", borderRadius: "12px", fontSize: "12px", fontWeight: 500 };
const partenaireRemoveStyle = { background: "none", border: "none", cursor: "pointer", color: "#0369a1", padding: "0 0 0 2px", fontSize: "13px", lineHeight: 1 };

function PartenairesEditor({ partenaires, newPartenaire, setNewPartenaire, addPartenaire, removePartenaire }) {
    return (
        <div style={{ marginBottom: "14px" }}>
            <label style={labelStyle}>Laboratoires partenaires</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px", marginBottom: "8px" }}>
                {partenaires.length === 0 && (
                    <span style={{ fontSize: "13px", color: "#9ca3af" }}>Aucun partenaire</span>
                )}
                {partenaires.map((pt, i) => (
                    <span key={i} style={partenaireChipStyle}>
                        {pt}
                        <button type="button" onClick={() => removePartenaire(i)} style={partenaireRemoveStyle}>×</button>
                    </span>
                ))}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
                <input
                    style={{ ...inputStyle, width: "260px" }}
                    placeholder="Nom du laboratoire (ex: BIOGARAN)"
                    value={newPartenaire}
                    onChange={e => setNewPartenaire(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addPartenaire(); } }}
                />
                <button type="button" onClick={addPartenaire} style={btnOutlineStyle}>Ajouter</button>
            </div>
        </div>
    );
}

export function PharmacyEditForm({ pharmacyId, editor }) {
    const { editForm, setEditForm } = editor;
    return (
        <form onSubmit={editor.handleEdit} style={{ ...insetFormStyle, padding: "20px", marginBottom: "8px" }}>
            <PharmacyFields editForm={editForm} setEditForm={setEditForm} />
            {isOrangeMailHost(editForm.mailHost) && (
                <p style={orangeNoticeStyle}>
                    Orange : utilisez le mot de passe dédié aux logiciels de messagerie, sans espaces, et activez l’accès POP/IMAP. Les messages sont relevés périodiquement.
                </p>
            )}
            <ServiceManager pharmacyId={pharmacyId} />
            <PartenairesEditor
                partenaires={editForm.partenaires}
                newPartenaire={editor.newPartenaire}
                setNewPartenaire={editor.setNewPartenaire}
                addPartenaire={editor.addPartenaire}
                removePartenaire={editor.removePartenaire}
            />
            {editor.editError && <p style={errorStyle}>{editor.editError}</p>}
            <div style={{ display: "flex", gap: "8px" }}>
                <button type="submit" disabled={editor.editSaving} style={btnBlackStyle}>
                    {editor.editSaving ? "Sauvegarde..." : "Sauvegarder"}
                </button>
                <button type="button" onClick={editor.cancelEdit} style={btnOutlineStyle}>Annuler</button>
            </div>
        </form>
    );
}
